import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { TableHeader, Modal, Details } from '@/components'
import { getFetchMoreStatus, sort } from '@/utils'
import { IPageInfo, OrderType } from '@/types'
import { t } from '@/i18n'
import {
  useDealHistoryQuery,
  DealDeprecatedHistoryNode,
} from '@/apollo/graphql'

import HistoryRow from './historyRow'

const columns = [
  {
    key: 'field_name',
    sortKey: 'fieldName',
  },
  {
    key: 'old_value',
    sortKey: 'oldValue',
  },
  {
    key: 'new_value',
    sortKey: 'newValue',
  },
  {
    key: 'date_and_time',
    sortKey: 'updateTime',
  },
  {
    key: 'update_user',
    sortKey: 'username',
  },
]

interface IUpdatesHistoryControl {
  showHide: (bool: boolean) => void
}

interface IUpdatesHistory {
  id?: string
  onRowClick: (item?: any) => void
}

const pageSize = 20

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

const UpdatesHistory = forwardRef<IUpdatesHistoryControl, IUpdatesHistory>(
  ({ onRowClick, id }, ref) => {
    const [sortKey, setKey] = useState<string>()
    const [orderBy, setOrderBy] = useState<OrderType>()
    const [updates, setUpdates] = useState<
      Partial<DealDeprecatedHistoryNode>[]
    >([])
    const [visible, setVisible] = useState(false)
    const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

    useImperativeHandle(
      ref,
      () => ({
        showHide: (bool: boolean) => setVisible(bool),
      }),
      [],
    )

    const { loading, networkStatus } = useDealHistoryQuery({
      fetchPolicy: 'cache-first',
      variables: {
        id: id || '',
        first: pageSize,
        offset: pageInfo?.offset || 0,
      },
      onCompleted: res => {
        if (res.deal?.dealdeprecatedhistorySet?.edges) {
          const d: Partial<DealDeprecatedHistoryNode>[] = []
          res.deal.dealdeprecatedhistorySet.edges.forEach(e => {
            // @ts-ignore
            if (e?.node) d.push(e.node)
          })
          setUpdates(prev => [...prev, ...d])
          setPageInfo(prev => ({
            ...prev,
            hasNextPage:
              res.deal?.dealdeprecatedhistorySet?.pageInfo.hasNextPage,
          }))
        }
      },
    })

    useEffect(() => {
      setPageInfo(prev => ({
        ...prev,
        fetchMoreStatus: getFetchMoreStatus({
          networkStatus,
          hasNextPage: prev?.hasNextPage,
          withForce: true,
        }),
      }))
    }, [networkStatus])

    const sortingBy = useCallback((key?: string, by?: OrderType) => {
      setKey(key)
      setOrderBy(by)
    }, [])

    const data = useMemo(() => {
      return updates
        .sort((a, b) => sort(a, b, sortKey, orderBy))
        .map(item => ({ ...item, fieldName: t(item.fieldName as any) }))
    }, [updates, sortKey, orderBy])

    if (!visible) return null

    return (
      <Modal
        visible={visible}
        title={t('updates_history')}
        onClose={() => setVisible(false)}
      >
        <Details
          title=""
          showNoData
          Wrapper="table"
          Row={HistoryRow}
          RowParent="tbody"
          onRowClick={() => {
            onRowClick()
            setVisible(false)
          }}
          loading={loading}
          wrapperClassName="scroll-wrap"
          dataBoxWrapperClassName="info-table"
          data={data}
          fetchMoreStatus={pageInfo.fetchMoreStatus}
          Header={() => (
            <TableHeader
              columns={columns}
              onSort={sortingBy}
              orderedBy={orderBy}
            />
          )}
          onFetchMore={() =>
            setPageInfo(prev => ({ ...prev, offset: updates.length }))
          }
        />
      </Modal>
    )
  },
)

export default UpdatesHistory
